import { createApi } from "@reduxjs/toolkit/query/react";
import { mainServiceURL } from "./defines";
// Move the logic that requires dispatch to a separate function
import { customTags } from "../../../redux/customTags";
import { baseQueryWithReauth } from "./defines/baseQueryReAuth";

interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginResponse {
  status: string;
}

interface RequestPasswordResetRequest {
  email: string;
}

interface ResetPasswordRequest {
  password: string;
  token: string;
}

interface PasswordResetResponse {
  status: string;
}

interface VerifyTokenResponse {
  status: string;
  isValid: boolean;
}

interface AuthModeResponse {
  data: {
    app: string;
    authType: string;
    isCognitoEnabled: boolean;
  };
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["AdminUser", ...customTags],
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: mainServiceURL + "/login/native",
        method: "POST",
        body: credentials,
      }),
    }),
    getUserDetails: builder.query({
      query: () => ({
        url: "users",
        method: "GET",
      }),
    }),
    getMenu: builder.query<any, void>({
      query: () => ({
        url: mainServiceURL + "/menu",
        method: "GET",
      }),
    }),
    verifyToken: builder.query<VerifyTokenResponse, void>({
      query: () => ({
        url: mainServiceURL + "/auth/verify",
        method: "GET",
      }),
      // Don't cache
      keepUnusedDataFor: 0,
    }),
    getAuthMode: builder.query<AuthModeResponse, void>({
      query: () => ({
        url: mainServiceURL + "/auth/mode",
        method: "GET",
      }),
      keepUnusedDataFor: 300,
    }),
    refreshToken: builder.mutation<void, void>({
      query: () => ({
        url: mainServiceURL + "/login/native/refresh",
        method: "POST",
      }),
    }),
    requestPasswordReset: builder.mutation<
      PasswordResetResponse,
      RequestPasswordResetRequest
    >({
      query: (data) => ({
        url: mainServiceURL + "/login/native/password-recovery",
        method: "POST",
        body: data,
      }),
    }),
    resetPassword: builder.mutation<
      PasswordResetResponse,
      ResetPasswordRequest
    >({
      query: (data) => ({
        url: mainServiceURL + "/login/native/password-reset",
        method: "POST",
        body: data,
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: mainServiceURL + "/logout/native",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetUserDetailsQuery,
  useGetMenuQuery,
  useVerifyTokenQuery,
  useGetAuthModeQuery,
  useLazyVerifyTokenQuery,
  useRefreshTokenMutation,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
